import { useModal } from "@/app/shared/modal-views/use-modal";
import UnderlineShape from "@/components/shape/underline";
import { PermissionsDataList } from "@/config/PermissionsLists";
import { EndPoints } from "@/Core/Client";
import { httpService } from "@/Core/Client/AxiosConfig";
import {
    RoleAssignDto,
    roleAssignSchema,
    RoleResponseDto,
} from "@/Dtos/role-dto";
import { Form } from "@/ui/form";
import axios from "axios";
import { useState } from "react";
import { Controller, SubmitHandler } from "react-hook-form";
import { PiCheckBold, PiXBold } from "react-icons/pi";
import {
    ActionIcon,
    AdvancedCheckbox,
    Button,
    CheckboxGroup,
    Title,
} from "rizzui";

interface Props {
    edit?: RoleResponseDto;
    callback?: () => void;
}

export default function AssignPermissionsForm({ edit, callback }: Props) {
    const { closeModal } = useModal();
    const [message, setMessage] = useState<string>();
    const [reset, setReset] = useState({});
    const [isLoading, setLoading] = useState(false);

    const onSubmit: SubmitHandler<RoleAssignDto> = async (data) => {
        setLoading(true);

        try {
            await httpService(EndPoints.role.assignPermission).update(
                edit!.id!,
                data,
            );
            callback?.();
            setLoading(false);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const { response } = error;
                if (response?.data?.message)
                    setMessage(response?.data?.message);
            }
            setLoading(false);
            return;
        }

        setReset;
        setLoading(false);
        closeModal();
    };

    return (
        <div className="m-auto p-4 md:px-5 md:py-6">
            <div className="mb-4 flex items-center justify-between">
                <Title as="h3" className="text-lg">
                    {/* Assign Permissions to Role */}
                    Role Access{" "}
                    <span className="relative inline-block uppercase text-lime-600">
                        {edit?.name}
                        <UnderlineShape className="absolute -bottom-2 -start-1 h-2.5 w-20 text-blue md:w-24 xl:-bottom-1.5 xl:w-32" />
                    </span>{" "}
                </Title>
                <ActionIcon
                    size="sm"
                    variant="text"
                    onClick={() => closeModal()}
                    className="p-0 text-gray-500 hover:!text-gray-900"
                >
                    <PiXBold className="h-[18px] w-[18px]" />
                </ActionIcon>
            </div>

            <Form<RoleAssignDto>
                validationSchema={roleAssignSchema}
                onSubmit={onSubmit}
                useFormProps={{
                    defaultValues: {
                        roleId: edit?.id ?? "",
                        permissions: edit?.permissions ?? [],
                    },
                }}
                className="grid grid-cols-1 gap-6 p-6 @container [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
            >
                {({ register, control, formState: { errors } }) => {
                    return (
                        <>
                            <div className="grid gap-4 divide-y divide-y-reverse divide-gray-200">
                                <input
                                    type="hidden"
                                    {...register("roleId")}
                                    value={edit?.id}
                                />

                                {PermissionsDataList.map(
                                    ({ role, permissions }) => {
                                        const parent = role.toLowerCase();
                                        return (
                                            <div
                                                key={role}
                                                className="flex flex-col gap-3 pb-4 md:flex-row md:items-center md:justify-between"
                                            >
                                                <Title
                                                    as="h6"
                                                    className="font-medium text-gray-700 2xl:text-sm"
                                                >
                                                    {role}
                                                </Title>
                                                <Controller
                                                    // @ts-ignore
                                                    name={"permissions"}
                                                    control={control}
                                                    render={({
                                                        field: {
                                                            onChange,
                                                            value,
                                                        },
                                                    }) => (
                                                        <CheckboxGroup
                                                            values={value}
                                                            setValues={onChange}
                                                            className="grid grid-cols-2 sm:grid-cols-3 gap-4 lg:flex text-xs md:text-sm"
                                                        >
                                                            {permissions.map(
                                                                (per) => (
                                                                    <AdvancedCheckbox
                                                                        key={`${role}.${per.name}`}
                                                                        name={`${parent}.${per.name.toLowerCase()}`}
                                                                        value={
                                                                            per.name
                                                                        }
                                                                        inputClassName="[&:checked~span>.icon]:block"
                                                                        contentClassName="flex items-center justify-center"
                                                                    >
                                                                        <PiCheckBold className="icon me-1 hidden h-[14px] w-[14px] md:h-4 md:w-4" />
                                                                        <span className="font-medium ">
                                                                            {per.name?.replace(
                                                                                "_",
                                                                                " ",
                                                                            )}
                                                                        </span>
                                                                    </AdvancedCheckbox>
                                                                ),
                                                            )}
                                                        </CheckboxGroup>
                                                    )}
                                                />
                                            </div>
                                        );
                                    },
                                )}
                            </div>
                            <div
                                className={
                                    "col-span-full flex items-center justify-end gap-4"
                                }
                            >
                                <Button
                                    variant="outline"
                                    className="w-full @xl:w-auto dark:hover:border-gray-400"
                                    // onClick={() => closeModal()}
                                    onClick={closeModal}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    isLoading={isLoading}
                                    type="submit"
                                    className="hover:gray-700 w-full @xl:w-auto"
                                >
                                    Save
                                </Button>
                            </div>
                        </>
                    );
                }}
            </Form>
        </div>
    );
}
